/**
 * Lab page (candidate view).
 *
 * The payload served to a candidate has no solution field at all — the
 * correction lives behind `GET /labs/{id}/solution`, which only instructors and
 * administrators may call. This page therefore cannot leak it, even by mistake.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Briefcase,
  CheckCircle2,
  Circle,
  ClipboardList,
  Clock,
  FileCheck2,
  HelpCircle,
  Package,
  ShieldCheck,
  Target,
  Workflow,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { CopyButton, MarkdownView, MermaidDiagram } from '@/components/content/MarkdownView';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/Progress';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth/AuthContext';
import { cn } from '@/lib/cn';
import { formatMinutes } from '@/lib/format';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { api } from '@/services/api';
import { fetchers, queryKeys } from '@/services/queries';
import type { LabStep } from '@/types/api';

export function LabPage() {
  const { labId = '' } = useParams();
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [done, setDone] = useState<number[]>([]);

  const lab = useQuery({
    queryKey: queryKeys.lab(labId),
    queryFn: () => fetchers.lab(labId),
    retry: false,
  });

  const saveProgress = useMutation({
    mutationFn: (payload: { status?: string; completed_steps?: number[] }) =>
      api.put(`/labs/${labId}/progress`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      void queryClient.invalidateQueries({ queryKey: queryKeys.progress });
    },
  });

  const steps = useMemo<LabStep[]>(() => (lab.data?.steps as LabStep[]) ?? [], [lab.data]);
  const percent = steps.length ? Math.round((done.length / steps.length) * 100) : 0;

  const toggleStep = (index: number) => {
    const next = done.includes(index) ? done.filter((item) => item !== index) : [...done, index];
    setDone(next);
    saveProgress.mutate({
      completed_steps: next,
      status: next.length === steps.length && steps.length > 0 ? 'completed' : 'in_progress',
    });
  };

  if (lab.isLoading) return <FullPageSpinner label="Chargement du TP…" />;
  if (lab.isError || !lab.data) return <NotFoundPage />;

  const data = lab.data;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/app/formations"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          Mes formations
        </Link>
        {hasRole('instructor', 'admin') ? (
          <Link
            to={`/app/formateur/tp/${data.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ai/30 bg-ai/10
                       px-3 py-1.5 text-xs font-semibold text-ai hover:bg-ai/15"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Voir la correction formateur
          </Link>
        ) : null}
      </div>

      <header>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="agent">
            <ClipboardList className="h-3 w-3" />
            Travaux pratiques
          </Badge>
          <Badge tone="neutral">{data.difficulty}</Badge>
          <Badge tone="neutral">
            <Clock className="h-3 w-3" />
            {formatMinutes(data.duration_minutes)}
          </Badge>
        </div>
        <h1 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">{data.title}</h1>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {data.professional_context ? (
            <Card className="border-tech/25 bg-tech/[0.05]">
              <CardBody>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-tech">
                  <Briefcase className="h-3.5 w-3.5" />
                  Contexte professionnel
                </p>
                <p className="mt-3 text-sm leading-relaxed text-fg/90">
                  {data.professional_context}
                </p>
              </CardBody>
            </Card>
          ) : null}

          {data.architecture_mermaid ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Workflow className="h-4 w-4 text-tech" />
                  Architecture cible
                </CardTitle>
              </CardHeader>
              <CardBody>
                <MermaidDiagram definition={data.architecture_mermaid} />
              </CardBody>
            </Card>
          ) : null}

          {data.statement_markdown ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Énoncé</CardTitle>
              </CardHeader>
              <CardBody>
                <MarkdownView source={data.statement_markdown} />
              </CardBody>
            </Card>
          ) : null}

          {/* Steps with per-step progress sent to the API */}
          {steps.length > 0 ? (
            <Card>
              <CardHeader className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle as="h2">Étapes ({steps.length})</CardTitle>
                <span className="text-xs font-semibold tabular-nums text-muted">
                  {done.length}/{steps.length} réalisée{done.length > 1 ? 's' : ''}
                </span>
              </CardHeader>
              <CardBody>
                <ProgressBar value={percent} label="Avancement du TP" tone="agent" />
                <ol className="mt-6 space-y-3">
                  {steps.map((step, index) => {
                    const completed = done.includes(index);
                    return (
                      <li
                        key={step.title}
                        className={cn(
                          'rounded-lg border p-4 transition-colors',
                          completed
                            ? 'border-ok/30 bg-ok/[0.05]'
                            : 'border-border bg-surface-2/40',
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => toggleStep(index)}
                            aria-pressed={completed}
                            aria-label={
                              completed
                                ? `Marquer l'étape ${index + 1} comme non réalisée`
                                : `Marquer l'étape ${index + 1} comme réalisée`
                            }
                            className="mt-0.5 shrink-0"
                          >
                            {completed ? (
                              <CheckCircle2 className="h-5 w-5 text-ok" />
                            ) : (
                              <Circle className="h-5 w-5 text-faint hover:text-tech" />
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                            <h3
                              className={cn(
                                'text-sm font-semibold',
                                completed ? 'text-muted line-through' : 'text-fg',
                              )}
                            >
                              <span className="mr-1.5 font-mono text-xs text-tech">
                                {index + 1}.
                              </span>
                              {step.title}
                            </h3>
                            <p className="mt-1.5 text-sm text-muted">{step.instructions}</p>

                            {step.commands && step.commands.length > 0 ? (
                              <ul className="mt-3 space-y-1.5">
                                {step.commands.map((command) => (
                                  <li
                                    key={command}
                                    className="flex items-start gap-2 rounded border border-border
                                               bg-[#0a0f1c] px-3 py-2"
                                  >
                                    <code className="min-w-0 flex-1 overflow-x-auto font-mono text-2xs
                                                     text-[#dbe4f3]">
                                      {command}
                                    </code>
                                    <CopyButton value={command} label="" />
                                  </li>
                                ))}
                              </ul>
                            ) : null}

                            {step.hint ? (
                              <details className="mt-3">
                                <summary
                                  className="cursor-pointer text-xs font-semibold text-warn
                                             hover:underline"
                                >
                                  Afficher l'indice
                                </summary>
                                <p className="mt-2 rounded border border-warn/25 bg-warn/[0.07]
                                              px-3 py-2 text-xs text-fg/90">
                                  {step.hint}
                                </p>
                              </details>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </CardBody>
            </Card>
          ) : null}

          {data.questions.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-ai" />
                  Questions à traiter dans votre rapport
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ol className="space-y-3">
                  {data.questions.map((question, index) => (
                    <li key={question.id ?? index} className="flex gap-3 text-sm">
                      <span className="shrink-0 font-mono text-xs font-bold text-ai">
                        Q{index + 1}
                      </span>
                      <span className="text-fg/90">{question.text}</span>
                    </li>
                  ))}
                </ol>
              </CardBody>
            </Card>
          ) : null}
        </div>

        <aside className="space-y-6">
          {data.objectives.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-tech" />
                  Objectifs
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="space-y-2">
                  {data.objectives.map((objective) => (
                    <li key={objective} className="flex gap-2 text-sm text-muted">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-hidden="true" />
                      {objective}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.prerequisites.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2">Prérequis</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="ml-4 list-disc space-y-1.5 text-sm text-muted">
                  {data.prerequisites.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.validation_criteria.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <FileCheck2 className="h-4 w-4 text-ok" />
                  Critères de validation
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="space-y-2">
                  {data.validation_criteria.map((criterion) => (
                    <li key={criterion} className="flex gap-2 text-sm text-muted">
                      <Circle className="mt-1 h-3 w-3 shrink-0 text-faint" aria-hidden="true" />
                      {criterion}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {data.deliverables.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-ai" />
                  Livrables attendus
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="ml-4 list-disc space-y-1.5 text-sm text-muted">
                  {data.deliverables.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {steps.length > 0 && done.length === steps.length ? (
            <Alert tone="success" title="TP terminé">
              Votre avancement est enregistré. Déposez vos livrables selon les consignes de votre
              formateur.
            </Alert>
          ) : null}

          <Button
            variant="outline"
            className="w-full"
            onClick={() => saveProgress.mutate({ status: 'completed', completed_steps: steps.map((_, i) => i) })}
            disabled={saveProgress.isPending}
          >
            <CheckCircle2 className="h-4 w-4" />
            Marquer le TP comme terminé
          </Button>
        </aside>
      </div>
    </div>
  );
}
